import { memo } from 'react';
import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import { analytics } from '@/lib/analytics';
import { useRecentScrobbles, type LastFmTrack } from '@/hooks/useRecentScrobbles';
import { isUsableAlbumArtUrl } from '@/lib/musicSleeve';

export type { LastFmTrack };

interface RecentlyScrobbledProps {
  username?: string;
  limit?: number;
}

export const RecentlyScrobbled = memo(function RecentlyScrobbled({
  username = 'cdbattags',
  limit = 10
}: RecentlyScrobbledProps) {
  const { data, isLoading, error } = useRecentScrobbles(username, limit);
  const tracks = data ?? [];
  const loading = isLoading && tracks.length === 0;
  const errorMessage = error instanceof Error ? error.message : error ? 'Failed to load tracks' : null;

  const handleTrackClick = (url?: string, name?: string) => {
    if (url) {
      analytics.lastfmTrackClicked(name ?? 'unknown', url);
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #1DB954 0%, #1ed760 100%)',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: 3,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          background: 'rgba(0, 0, 0, 0.2)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: '#1ed760',
              animation: tracks.some(t => t['@attr']?.nowplaying === 'true')
                ? 'pulse 2s infinite'
                : 'none',
              '@keyframes pulse': {
                '0%, 100%': {
                  opacity: 1,
                  transform: 'scale(1)',
                },
                '50%': {
                  opacity: 0.5,
                  transform: 'scale(1.2)',
                },
              },
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: 700,
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            Now Playing
          </Typography>
        </Stack>
      </Box>

      {/* Track List */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          bgcolor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.4)',
            },
          },
        }}
      >
        {/* Loading State */}
        {loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: 'white' }} size={48} />
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              Loading {username}'s jams...
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {errorMessage && !loading && (
          <Box sx={{ p: 2 }}>
            <Alert
              severity="error"
              sx={{
                bgcolor: 'rgba(211, 47, 47, 0.2)',
                color: 'white',
                '& .MuiAlert-icon': {
                  color: 'white',
                },
              }}
            >
              {errorMessage}
            </Alert>
          </Box>
        )}

        {/* Track List */}
        {!loading && !errorMessage && tracks.map((track, index) => {
          const albumArt = isUsableAlbumArtUrl(track.image[1]?.['#text'])
            ? track.image[1]['#text']
            : '/assets-cdbattaglia/media/images/compact-disc.svg';
          const isNowPlaying = track['@attr']?.nowplaying === 'true';
          // Use timestamp or fallback to name+artist+index for stable key
          const trackKey = track.date?.uts || `${track.name}-${track.artist['#text']}-${index}`;

          return (
            <Box
              key={trackKey}
              onClick={() => handleTrackClick(track.url, track.name)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                cursor: track.url ? 'pointer' : 'default',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                bgcolor: isNowPlaying
                  ? 'rgba(30, 215, 96, 0.2)'
                  : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
                '&:last-child': {
                  borderBottom: 'none',
                },
              }}
            >
              {/* Album Artwork */}
              <Box
                sx={{
                  position: 'relative',
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={albumArt}
                  alt={track.album?.['#text'] || track.name}
                  onError={(event) => {
                    event.currentTarget.src = '/assets-cdbattaglia/media/images/compact-disc.svg';
                  }}
                  sx={{
                    width: 64,
                    height: 64,
                    objectFit: 'cover',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                />
                {isNowPlaying && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: '#1ed760',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 8px rgba(30, 215, 96, 0.8)',
                      animation: 'pulse 2s infinite',
                    }}
                  >
                    <Typography sx={{ fontSize: '10px', lineHeight: 1 }}>
                      ▶
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Track Info */}
              <Stack
                sx={{
                  ml: 2,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="700"
                  noWrap
                  sx={{
                    color: 'white',
                    fontSize: '1rem',
                    lineHeight: 1.4,
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  {track.name}
                </Typography>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.9rem',
                    lineHeight: 1.4,
                  }}
                >
                  {track.artist['#text']}
                </Typography>
                {isNowPlaying && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '2px',
                        height: 12,
                        alignItems: 'flex-end',
                      }}
                    >
                      {[...Array(4)].map((_, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 3,
                            bgcolor: '#1ed760',
                            borderRadius: '2px',
                            animation: `bars 1s ease-in-out infinite`,
                            animationDelay: `${i * 0.1}s`,
                            '@keyframes bars': {
                              '0%, 100%': { height: '4px' },
                              '50%': { height: '12px' },
                            },
                          }}
                        />
                      ))}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#1ed760',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Playing Now
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
});
